import { useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Hook to provide theme-aware CSS class utilities
 * This helps components use Tailwind classes with dynamic theme support
 */
export const useThemeClasses = () => {
  const { theme } = useTheme();

  return useMemo(() => ({
    // Button variants
    button: {
      primary: 'btn-theme-primary',
      secondary: 'btn-theme-secondary', 
      outline: 'btn-theme-outline',
      ghost: 'btn-theme-ghost',
      send: 'btn-theme-send',
      danger: 'btn-danger', // Keep legacy for non-theme colors
      success: 'btn-success', // Keep legacy for non-theme colors
    },

    // Button sizes
    buttonSize: {
      xs: 'px-2.5 py-1.5 text-xs sm:px-3 min-h-[32px]',
      sm: 'btn-sm',
      md: 'btn-md',
      lg: 'btn-lg',
      xl: 'px-8 py-3 text-base sm:px-12 sm:py-5 sm:text-xl min-h-[56px] sm:min-h-[64px]',
    },

    // Button shapes (DO THIS/NOT THAT: All buttons should have sharp corners except circle)
    buttonShape: {
      rectangle: 'sharp-corners', // No rounded corners (border-radius: 0)
      rounded: 'sharp-corners', // Changed to sharp corners for DO THIS compliance
      pill: 'rounded-full', // Keep pill shape for legacy support
      circle: 'rounded-full aspect-square p-0', // Circle buttons need full rounding
    },

    // Form fields
    field: {
      inactive: 'form-field-inactive',
      active: 'form-field-active', 
      error: 'form-field-error',
    },

    // Form controls
    form: {
      checkbox: 'accent-blue-600 focus:ring-blue-500 border-gray-300',
      radio: 'text-blue-600 focus:ring-blue-500 border-gray-300',
      slider: 'bg-gray-200 appearance-none cursor-pointer',
    },

    // Cards
    card: {
      base: 'card-theme',
      selected: 'card-theme-selected',
      interactive: 'card-theme-interactive',
      header: 'card-theme-header',
      body: 'card-theme-body', 
      footer: 'card-theme-footer',
    },

    // Text colors
    text: {
      primary: 'theme-text-primary',
      secondary: 'theme-text-secondary',
      onPrimary: 'theme-text-on-primary',
      accent: 'theme-text-secondary', // Map to secondary for now
      error: 'text-red-500',
      warning: 'text-amber-600',
      muted: 'text-gray-500',
    },

    // Background colors
    bg: {
      primary: 'theme-bg-primary',
      secondary: 'theme-bg-secondary',
      surface: 'theme-bg-surface',
      inactive: 'theme-bg-inactive',
      active: 'theme-bg-active',
      sendButton: 'theme-bg-send-button',
      accent: 'theme-bg-surface', // Map to surface for now
      muted: 'theme-bg-surface', // Map to surface for now
      error: 'bg-red-50',
      warning: 'bg-amber-50',
    },

    // Border colors
    border: {
      primary: 'theme-border-primary',
      inactive: 'theme-border-inactive',
      active: 'theme-border-active',
      accent: 'theme-border-inactive', // Map to inactive for now
      input: 'theme-border-inactive', // Map to inactive for form inputs
      error: 'border-red-200',
      warning: 'border-amber-200',
    },

    // Focus styles
    focus: {
      ring: 'focus:ring-4 focus:ring-blue-200',
      border: 'focus:border-blue-500',
    },

    // Additional utility classes
    placeholder: 'placeholder:text-gray-400',

    // Typography
    font: {
      primary: 'theme-font-primary',
      secondary: 'theme-font-secondary',
    },

    // Common combinations for quick use
    combinations: {
      // Primary button
      primaryButton: 'btn-theme-primary btn-md rounded-lg',
      
      // Secondary button  
      secondaryButton: 'btn-theme-secondary btn-md rounded-lg',
      
      // Outline button
      outlineButton: 'btn-theme-outline btn-md rounded-lg',
      
      // Ghost button
      ghostButton: 'btn-theme-ghost btn-md rounded-lg',
      
      // Interactive card
      interactiveCard: 'card-theme-interactive',
      
      // Selected card
      selectedCard: 'card-theme-selected',
      
      // Form input (inactive state)
      formInput: 'form-field-inactive',
      
      // Form input (active/focused state) 
      formInputActive: 'form-field-active',
      
      // Primary text
      primaryText: 'theme-text-primary theme-font-primary',
      
      // Secondary text
      secondaryText: 'theme-text-secondary theme-font-primary',
      
      // Modal overlay
      modalOverlay: 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50',
      
      // Modal content
      modalContent: 'theme-bg-surface rounded-xl shadow-strong max-w-4xl w-full max-h-[90vh] overflow-hidden',
      
      // Loading spinner with theme colors
      spinner: 'animate-spin rounded-full border-2 theme-border-inactive border-t-theme-border-primary',
    }
  }), [theme]);
};

/**
 * Utility function to combine classes conditionally
 * Usage: cn('base-class', condition && 'conditional-class', 'another-class')
 */
export const cn = (...classes: (string | boolean | undefined | null)[]): string => {
  return classes.filter(Boolean).join(' ');
};

export default useThemeClasses;